import React, { useEffect, useRef, useState } from "react";
import "./features.css";

export default function Features() {
  const features = [
    {
      image: "/third.svg",
      title: "About Evide Bus: Travel Safe Travel Smart",
      description: (
        <>
          <p className="intro-text">
            No more waiting endlessly at bus stops. With Evide Bus, you can find
            the nearest buses and their timings in real time right in front of
            you without the need to ask anyone. We are introducing a new way to
            simplify daily travel for people in Trivandrum by making private bus
            timings easily accessible.
          </p>
          <ul className="intro-steps">
            <li>Evide Bus web application</li>
            <li>Search for your route / bus stop</li>
            <li>Check upcoming bus timings</li>
            <li>Plan your journey without wasting time waiting</li>
          </ul>
          <p className="intro-closing">
            So, don't miss your bus again – travel smart with Evide Bus. 🚍✨
          </p>
        </>
      ),
    },
    {
      image: "/about.svg",
      title: "Smart Search Options",
      description: `Evide Bus is designed to make everyday bus travel across Trivandrum simpler, smarter, and more reliable. Our platform allows passengers to quickly find accurate private bus timings and routes without confusion. The application comes with a seamless search experience, providing two powerful ways to explore journeys. Users can search by route by entering their journey's starting point, destination, and date of travel to instantly get a list of buses operating on that route. Each result shows the bus name, number, expected arrival times at the selected stops, journey duration, and the days on which the service is available. For those who prefer to check schedules from a particular stop, the "search by stop" option enables passengers to view all buses passing through their chosen location along with the arrival time of each service, making it easier to plan trips efficiently.`,
    },
    {
      image: "/first.svg",
      title: "Full Journey Details",
      description: `Every bus in Evide Bus comes with a detailed timetable that displays the complete route, all intermediate stops, and the corresponding timings. Passengers can not only view the starting and ending times but also get clarity on the entire journey, ensuring better planning and time management. The timetable also highlights weekly availability, showing whether a bus operates daily, only on weekdays, or exclusively on weekends.`,
    },
    {
      image: "/sec.svg",
      title: "Convenient & Reliable",
      description: `Evide Bus is built with the goal of transforming the way people in Trivandrum access and depend on bus information. Beyond just displaying schedules, it brings together convenience, reliability, and innovation to create a smooth travel planning experience. With flexible features such as personalized favourites, multilingual access, and clear route visualisations, the platform ensures that every passenger finds information quickly and effortlessly.`,
    },
  ];

  const boxRefs = useRef([]);
  const routeRef = useRef(null);
  const [visible, setVisible] = useState(() => features.map(() => false));
  const [progress, setProgress] = useState(0);

  // Reveal each stop once it scrolls into view
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      setVisible(features.map(() => true));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.index);
            setVisible((prev) => {
              if (prev[idx]) return prev;
              const next = [...prev];
              next[idx] = true;
              return next;
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.22, rootMargin: "0px 0px -10% 0px" }
    );

    boxRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [features]);

  // Fill the route line in sync with scroll position — one connected journey
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) {
      setProgress(1);
      return;
    }

    let frame = null;
    const handleScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        const el = routeRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const scrolled = vh * 0.8 - rect.top;
        const pct = Math.min(1, Math.max(0, scrolled / rect.height));
        setProgress(pct);
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <section id="features" className="evidebus-features">
      <div className="features-intro">
        <p className="features-kicker">How Evide Bus works</p>
        <h4 className="features-heading">
          Every stop on your journey, sorted
        </h4>
        <p className="features-subheading">
          From finding a bus to knowing exactly when it'll reach your stop —
          here's what happens along the way.
        </p>
      </div>

      <div className="features-route" ref={routeRef}>
        <div className="route-line" aria-hidden="true">
          <div
            className="route-line-fill"
            style={{ transform: `scaleY(${progress})` }}
          />
        </div>

        {features.map((feature, index) => (
          <div
            key={index}
            ref={(el) => (boxRefs.current[index] = el)}
            data-index={index}
            className={`feature-box ${index % 2 === 1 ? "reverse" : ""} ${
              visible[index] ? "is-visible" : ""
            }`}
          >
            <span className="route-stop" aria-hidden="true">
              <span className="route-stop-dot" />
              <span className="route-stop-index">
                Stop {String(index + 1).padStart(2, "0")}
              </span>
            </span>

            <div className="feature-text">
              <h5 className="feature-title">{feature.title}</h5>
              <div className="feature-description">
                {typeof feature.description === "string" ? (
                  <p>{feature.description}</p>
                ) : (
                  feature.description
                )}
              </div>
            </div>

            <div className="feature-image">
              <div className="feature-image-frame">
                <img src={feature.image} alt={feature.title} loading="lazy" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}